import { useState } from 'react';

interface TimeSelectionProps {
  selectedDate: string;
  selectedTime: string;
  onDateSelect: (date: string) => void;
  onTimeSelect: (time: string) => void;
  availableTimes?: string[];
}

export default function TimeSelection({ selectedDate, selectedTime, onDateSelect, onTimeSelect, availableTimes }: TimeSelectionProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Generate calendar dates
  const generateCalendarDates = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const dates = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      dates.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      dates.push(new Date(year, month, day));
    }
    
    return dates;
  };

  const dates = generateCalendarDates();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const allTimeSlots = [
    { time: '09:00', period: '오전' },
    { time: '10:00', period: '오전' },
    { time: '11:00', period: '오전' },
    { time: '12:00', period: '오후' },
    { time: '13:00', period: '오후' },
    { time: '14:00', period: '오후' },
    { time: '15:00', period: '오후' },
    { time: '16:00', period: '오후' },
    { time: '17:00', period: '저녁' },
    { time: '18:00', period: '저녁' },
    { time: '19:00', period: '저녁' },
    { time: '20:00', period: '저녁' }
  ];

  const isTimeAvailable = (time: string) => {
    if (!availableTimes) return true;
    return availableTimes.includes(time);
  };

  const handleDateClick = (date: Date) => {
    const dateString = date.toISOString().split('T')[0];
    onDateSelect(dateString);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const isDateSelected = (date: Date | null) => {
    if (!date || !selectedDate) return false;
    return date.toISOString().split('T')[0] === selectedDate;
  };

  const isDateDisabled = (date: Date | null) => {
    if (!date) return true;
    return date < today;
  };

  return (
    <section className="mb-12">
      <div className="bg-white rounded-3xl p-8 shadow-lg">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-r from-rose-400 to-pink-400 rounded-full">
            <i className="ri-calendar-line text-white text-lg"></i>
          </div>
          <div>
            <h2 className="text-2xl font-medium text-gray-800">날짜 & 시간 선택</h2>
            <p className="text-sm text-gray-600">예약하실 날짜와 시간을 선택해주세요</p>
          </div>
        </div>

        {/* Calendar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-800">
              {currentMonth.getFullYear()}년 {currentMonth.getMonth() + 1}월
            </h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={handlePrevMonth}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-rose-50 transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-gray-600 text-lg"></i>
              </button>
              <button
                onClick={handleNextMonth}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-rose-50 transition-all cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-gray-600 text-lg"></i>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2">
            {['일', '월', '화', '수', '목', '금', '토'].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-gray-600 py-2">
                {day}
              </div>
            ))}
            {dates.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="aspect-square"></div>;
              }

              const isSelected = isDateSelected(date);
              const isDisabled = isDateDisabled(date);

              return (
                <button
                  key={date.toISOString()}
                  onClick={() => !isDisabled && handleDateClick(date)}
                  disabled={isDisabled}
                  className={`aspect-square rounded-xl text-sm font-medium transition-all ${
                    isDisabled
                      ? 'text-gray-300 cursor-not-allowed'
                      : isSelected
                      ? 'bg-gradient-to-r from-rose-400 to-pink-400 text-white shadow-md cursor-pointer'
                      : 'text-gray-700 hover:bg-rose-50 cursor-pointer'
                  }`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Slots */}
        {selectedDate && (
          <div>
            <h3 className="text-lg font-medium text-gray-800 mb-4">시간 선택</h3>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {allTimeSlots.map((slot) => {
                const isAvailable = isTimeAvailable(slot.time);
                const isSelected = selectedTime === slot.time;
                
                return (
                  <button
                    key={slot.time}
                    onClick={() => isAvailable && onTimeSelect(slot.time)}
                    disabled={!isAvailable}
                    className={`py-3 px-4 rounded-xl text-sm font-medium transition-all ${
                      !isAvailable
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-2 border-gray-200'
                        : isSelected
                        ? 'bg-gradient-to-r from-rose-400 to-pink-400 text-white shadow-md cursor-pointer border-2 border-transparent'
                        : 'bg-white text-gray-700 hover:bg-rose-50 cursor-pointer border-2 border-gray-200 hover:border-rose-300'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <span className={`text-xs mb-1 ${
                        !isAvailable ? 'text-gray-400' : isSelected ? 'text-white/80' : 'text-gray-500'
                      }`}>
                        {slot.period}
                      </span>
                      <span>{slot.time}</span>
                      {!isAvailable && (
                        <span className="text-xs mt-1 text-gray-400">예약불가</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
